// frontend/src/utils/referral.ts
//
// Bar-wedge acquisition attribution. /bar/<ref> stores the ref here; signup
// reads it and sends it as referral_source. First touch wins: a later scan
// never overwrites an existing stored ref. 7-day attribution window.

const STORAGE_KEY = 'referral_source';
const ATTRIBUTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const REF_SLUG_REGEX = /^[a-z0-9][a-z0-9_-]*$/;

interface StoredReferral {
  ref: string;
  ts: number;
}

export const normalizeRef = (raw: string | null | undefined): string | null => {
  const ref = (raw ?? '').trim().toLowerCase().slice(0, 64);
  return REF_SLUG_REGEX.test(ref) ? ref : null;
};

export const saveReferral = (raw: string | null | undefined): void => {
  const ref = normalizeRef(raw);
  if (!ref) return;
  try {
    if (getActiveReferral()) return; // first touch wins
    const payload: StoredReferral = { ref, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // storage unavailable (private mode etc.) - attribution is best-effort
  }
};

export const getActiveReferral = (): string | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReferral;
    if (!parsed?.ref || !parsed?.ts) return null;
    if (Date.now() - parsed.ts > ATTRIBUTION_WINDOW_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return normalizeRef(parsed.ref);
  } catch {
    return null;
  }
};

export const clearReferral = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

// --- User share refs ('u_<userId>') -------------------------------------
// Person-to-person share attribution (the Edge-1 share vehicle). Same slug
// grammar and storage as bar refs, so the whole first-touch pipeline
// (saveReferral -> signup referral_source -> analytics) applies unchanged.

export const USER_REF_PREFIX = 'u_';

export const userShareRef = (userId: number | string): string =>
  `${USER_REF_PREFIX}${userId}`;

export const isUserRef = (ref: string | null | undefined): boolean =>
  !!ref && ref.startsWith(USER_REF_PREFIX);
